import { useMemo } from 'react';
import { Label, PolarRadiusAxis, RadialBar, RadialBarChart } from 'recharts';
import { LabelValuePair } from '~/hooks/api/insights/useGetIGInsights';

import { ChartContainer } from '../chart';

export interface GenderChartTheme {
  primary?: string;
  secondary?: string;
  tertiary?: string;
  background?: string;
  backgroundAlpha?: string;
  textBase?: string;
  textSubtle?: string;
}

const DEFAULT_COLORS = {
  primary: '#155FEF',
  secondary: '#80A9F9',
  tertiary: '#CCDDFF',
  background: '#ffffff',
  backgroundAlpha: '#dee2e8',
  textBase: '#15191e',
  textSubtle: '#303d4f',
};

interface GenderChartProps {
  genders?: LabelValuePair[] | null;
  theme?: GenderChartTheme;
}

export const GenderChart = ({ genders, theme }: GenderChartProps) => {
  const colors = {
    primary: theme?.primary ?? DEFAULT_COLORS.primary,
    secondary: theme?.secondary ?? DEFAULT_COLORS.secondary,
    tertiary: theme?.tertiary ?? DEFAULT_COLORS.tertiary,
    background: theme?.background ?? DEFAULT_COLORS.background,
    backgroundAlpha: theme?.backgroundAlpha ?? DEFAULT_COLORS.backgroundAlpha,
    textBase: theme?.textBase ?? DEFAULT_COLORS.textBase,
    textSubtle: theme?.textSubtle ?? DEFAULT_COLORS.textSubtle,
  };
  const genderValue = useMemo(() => {
    const male = genders?.find(
      (g: { label: string }) => g.label.toLowerCase() === 'male',
    );
    const female = genders?.find(
      (g: { label: string }) => g.label.toLowerCase() === 'female',
    );
    const other = genders?.find(
      (g: { label: string }) =>
        g.label.toLowerCase() === 'other' ||
        g.label.toLowerCase() === 'unknown',
    );

    return {
      male: Math.round((male?.value || 0) * 100),
      female: Math.round((female?.value || 0) * 100),

      // keep `other` if the value is a number. Applies to tiktok & snapchat insights
      other:
        typeof other?.value === 'number'
          ? Math.round(other.value * 100)
          : undefined,
    };
  }, [genders]);

  const chartData = [genderValue];

  const getHighestGender = (): {
    key: 'male' | 'female' | 'other';
    percentage: number;
    label: string;
    color: string;
  } => {
    const gendersList: {
      key: 'male' | 'female' | 'other';
      percentage: number;
      label: string;
      color: string;
    }[] = [
      {
        key: 'male',
        percentage: genderValue.male,
        label: 'Male',
        color: colors.primary,
      },
      {
        key: 'female',
        percentage: genderValue.female,
        label: 'Female',
        color: colors.secondary,
      },
    ];

    if (genderValue.other !== undefined) {
      gendersList.push({
        key: 'other',
        percentage: genderValue.other,
        label: 'Other',
        color: colors.tertiary,
      });
    }

    return gendersList.reduce((max, current) =>
      current.percentage > max.percentage ? current : max,
    );
  };

  const highestGender = getHighestGender();
  const displayData = {
    percentage: highestGender.percentage,
    label: highestGender.label,
  };

  const otherSegments = useMemo(() => {
    const segments = [];

    if (highestGender.key !== 'male') {
      segments.push({
        label: 'Male',
        percentage: genderValue.male,
        color: colors.primary,
      });
    }
    if (highestGender.key !== 'female') {
      segments.push({
        label: 'Female',
        percentage: genderValue.female,
        color: colors.secondary,
      });
    }
    if (genderValue.other !== undefined && highestGender.key !== 'other') {
      segments.push({
        label: 'Other',
        percentage: genderValue.other,
        color: colors.tertiary,
      });
    }

    return segments;
  }, [genderValue, highestGender.key, colors]);

  return (
    <div
      className="flex h-[280px] min-w-[180px] flex-[1_1_180px] flex-col rounded-12 border p-4"
      style={{
        backgroundColor: colors.background,
        borderColor: colors.backgroundAlpha,
      }}
    >
      <p className="text-16 font-500" style={{ color: colors.textBase }}>
        Gender distribution
      </p>
      <ChartContainer
        config={{
          male: {
            label: 'Male',
            color: colors.primary,
          },
          female: {
            label: 'Female',
            color: colors.secondary,
          },
        }}
        className="mx-auto h-[180px] w-full overflow-visible outline-none [&_*]:outline-none"
      >
        <div className="flex flex-col items-center justify-center">
          <RadialBarChart
            width={280}
            height={160}
            data={chartData}
            startAngle={180}
            endAngle={0}
            innerRadius={70}
            outerRadius={110}
            cy="70%"
          >
            <PolarRadiusAxis tick={false} tickLine={false} axisLine={false}>
              <Label
                content={({ viewBox }) => {
                  if (viewBox && 'cx' in viewBox && 'cy' in viewBox) {
                    return (
                      <text x={viewBox.cx} y={viewBox.cy} textAnchor="middle">
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) - 14}
                          className="text-16 font-700"
                          fill={colors.textBase}
                        >
                          {displayData.percentage}%
                        </tspan>
                        <tspan
                          x={viewBox.cx}
                          y={(viewBox.cy || 0) + 12}
                          className="text-16"
                          fill={colors.textSubtle}
                        >
                          {displayData.label}
                        </tspan>
                      </text>
                    );
                  }
                }}
              />
            </PolarRadiusAxis>
            <RadialBar
              dataKey="male"
              stackId="a"
              cornerRadius={5}
              fill={colors.primary}
              className="stroke-2"
            />
            <RadialBar
              dataKey="female"
              fill={colors.secondary}
              stackId="a"
              cornerRadius={5}
              className="stroke-2"
            />
            {genderValue.other !== undefined && (
              <RadialBar
                dataKey="other"
                fill={colors.tertiary}
                stackId="a"
                cornerRadius={5}
                className="stroke-2"
              />
            )}
          </RadialBarChart>
          <div className="flex w-full flex-col items-center gap-2">
            {otherSegments.map((segment) => (
              <div key={segment.label} className="flex items-center gap-2">
                <div
                  className="size-3 rounded-0p5"
                  style={{ backgroundColor: segment.color }}
                />
                <span className="text-14" style={{ color: colors.textBase }}>
                  {segment.label}
                </span>
                <span
                  className="text-14 font-500"
                  style={{ color: colors.textBase }}
                >
                  {segment.percentage}%
                </span>
              </div>
            ))}
          </div>
        </div>
      </ChartContainer>
    </div>
  );
};
